import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase/supabase';
import './OTPVerification.css';
import { motion } from 'framer-motion';

const OTPVerification = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { email, phoneNumber, method } = location.state || {}; // method: 'email', 'sms', 'whatsapp'

    const [otp, setOtp] = useState(['', '', '', '']);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendTimer, setResendTimer] = useState(30);

    useEffect(() => {
        if (!email && !phoneNumber) {
            navigate('/');
        }
    }, [email, phoneNumber, navigate]);

    useEffect(() => {
        let interval;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimer]);

    const handleChange = (element, index) => {
        if (isNaN(element.value)) return;

        const newOtp = [...otp];
        newOtp[index] = element.value;
        setOtp(newOtp);

        // Focus next input
        if (element.value && index < 3) {
            document.getElementById(`otp-input-${index + 1}`).focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            document.getElementById(`otp-input-${index - 1}`).focus();
        }
    };

    const verifyOtp = async () => {
        const code = otp.join('');
        if (code.length !== 4) {
            setError('Please enter a 4-digit code.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // 1. Verify OTP via Edge Function
            const payload = email ? { email, otpCode: code } : { phoneNumber, otpCode: code };

            const { data, error: funcError } = await supabase.functions.invoke('phone-login-otp-verify', {
                body: payload
            });

            if (funcError || !data?.success) {
                throw new Error(data?.error || funcError?.message || 'Invalid OTP');
            }

            // 2. Complete Authentication with Supabase
            const { data: authData, error: authError } = await supabase.auth.verifyOtp({
                token_hash: data.token,
                type: 'magiclink'
            });

            if (authError) throw authError;

            // 3. Redirect
            navigate('/feed');

        } catch (err) {
            console.error('OTP Verification Error:', err);
            setError(err.message || 'Verification failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (resendTimer > 0) return;

        setLoading(true);
        setError('');

        try {
            let endpoint = '';
            let payload = {};

            if (email) {
                endpoint = 'email-login-otp-send';
                payload = { email };
            } else {
                endpoint = 'phone-login-otp-send';
                payload = { phoneNumber, method: method || 'sms' };
            }

            const { data, error } = await supabase.functions.invoke(endpoint, {
                body: payload
            });

            if (error) throw error;

            setResendTimer(30);
            alert('Code sent successfully!');

        } catch (err) {
            console.error('Resend Error:', err);
            setError('Failed to resend code.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="otp-container">
            <motion.div
                className="otp-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <h2>Verify it's you</h2>
                <p className="otp-subtitle">
                    We sent a code to {email || phoneNumber}.<br />
                    Enter it below to sign in.
                </p>

                <div className="otp-inputs">
                    {otp.map((data, index) => (
                        <input
                            key={index}
                            id={`otp-input-${index}`}
                            type="text"
                            maxLength="1"
                            value={data}
                            onChange={(e) => handleChange(e.target, index)}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            onFocus={(e) => e.target.select()}
                        />
                    ))}
                </div>

                {error && <p className="otp-error">{error}</p>}

                <button
                    className="otp-verify-btn"
                    onClick={verifyOtp}
                    disabled={loading || otp.join('').length !== 4}
                >
                    {loading ? 'Verifying...' : 'Verify & Sign In'}
                </button>

                <p className="resend-text">
                    Didn't verify code?{' '}
                    <span
                        className={`resend-link ${resendTimer > 0 ? 'disabled' : ''}`}
                        onClick={handleResend}
                    >
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
                    </span>
                </p>

                <button className="back-btn" onClick={() => navigate('/')}>
                    Back to Login
                </button>
            </motion.div>
        </div>
    );
};

export default OTPVerification;
